import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { NEVER, throwError } from 'rxjs';
import { catchError, mergeMap, take, tap } from 'rxjs/operators';
import { v4 as guidv4 } from 'uuid';
import { ServerEventsProviderService } from '@anecdotes/server-events';
import { LoggerService } from '@anecdotes/utils';
import { CommentModel, ServerEventType } from '../../../models';
import { OperationTrackerService } from '../../../services';
import { CommentApiService } from '../../../services/api';
import { CommentAction } from '../../actions';
import { CommentSelectors } from '../../selectors';
import { CommentingState } from '../../state';

@Injectable()
export class CommentEffects {
  constructor(
    private actions: Actions,
    private commentApiService: CommentApiService,
    private operationTrackerService: OperationTrackerService,
    private store: Store<CommentingState>,
    private serverEventsProviderService: ServerEventsProviderService,
    private logger: LoggerService,
  ) {}

  loadAllComments$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(CommentAction.LoadAll),
        mergeMap(() => this.commentApiService.getAllComments()),
        tap((response) => this.store.dispatch(CommentAction.AllLoaded({ comments: response }))),
      ),
    { dispatch: false },
  );

  optimisticCreateComment$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(CommentAction.OptimisticCreate),
        mergeMap((action) => {
          // Optimistic comment creation
          const temporaryId = guidv4();
          const comment = {
            id: temporaryId,
            threadId: action.comment.threadId,
            body: action.comment.body,
            commenterId: action.comment.commenterId,
            commenterName: action.comment.commenterName,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            isCreationInProgress: true,
          };
          this.store.dispatch(CommentAction.Created({ comment }));

          return this.operationTrackerService.passThroughOperationTracking(
            action.operationId,
            this.commentApiService.createComment(action.comment).pipe(
              tap((createdComment) => {
                return this.store.dispatch(
                  CommentAction.Updated({
                    updatedComment: { ...createdComment, isCreationInProgress: false },
                    id: temporaryId,
                  }),
                );
              }),
              catchError((err) => {
                this.store.dispatch(CommentAction.Deleted({ deletedComment: comment }));
                this.store.dispatch(
                  CommentAction.CreateFailed({
                    comment: action.comment,
                  }),
                );
                return throwError(err);
              }),
            ),
          );
        }),
        catchError(() => NEVER),
      ),
    { dispatch: false },
  );

  createComment$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(CommentAction.Create),
        mergeMap((action) =>
          this.operationTrackerService.passThroughOperationTracking(
            action.operationId,
            this.commentApiService.createComment(action.comment).pipe(
              tap((createdComment) =>
                this.store.dispatch(
                  CommentAction.Created({
                    comment: createdComment,
                  }),
                ),
              ),
              catchError((err) => {
                this.store.dispatch(
                  CommentAction.CreateFailed({
                    comment: action.comment,
                  }),
                );
                return throwError(err);
              }),
            ),
          ),
        ),
        catchError(() => NEVER),
      ),
    { dispatch: false },
  );

  updateCommentBody$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(CommentAction.UpdateBody),
        mergeMap((action) =>
          this.operationTrackerService.passThroughOperationTracking(
            action.operationId,
            this.store.select(CommentSelectors.createSelectorById(action.commentId)).pipe(
              take(1),
              mergeMap((commentBeforeUpdate) => {
                const updatedComment: CommentModel = {
                  ...commentBeforeUpdate,
                  body: action.newBody,
                  mentionedEmails: action.mentionedEmails,
                  extraParams: action.extraParams,
                };
                this.store.dispatch(CommentAction.Updated({ updatedComment }));

                return this.commentApiService.updateComment(updatedComment).pipe(
                  catchError((err) => {
                    this.store.dispatch(
                      CommentAction.UpdateFailed({
                        commentBeforeUpdate: commentBeforeUpdate,
                      }),
                    );
                    return throwError(err);
                  }),
                );
              }),
            ),
          ),
        ),
        catchError(() => NEVER),
      ),
    { dispatch: false },
  );

  deleteComment$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(CommentAction.Delete),
        mergeMap((action) =>
          this.operationTrackerService.passThroughOperationTracking(
            action.operationId,
            this.store.select(CommentSelectors.createSelectorById(action.commentId)).pipe(
              take(1),
              mergeMap((deletedComment) => {
                this.store.dispatch(CommentAction.Deleted({ deletedComment }));

                return this.commentApiService.deleteComment(action.commentId).pipe(
                  catchError((err) => {
                    this.store.dispatch(
                      CommentAction.DeleteFailed({
                        deletedComment: deletedComment,
                      }),
                    );
                    return throwError(err);
                  }),
                );
              }),
              catchError(() => NEVER),
            ),
          ),
        ),
      ),
    { dispatch: false },
  );

  commentCreatedEvent$ = createEffect(() =>
    this.serverEventsProviderService.getServerEvent(ServerEventType.CommentCreated).pipe(
      tap((serverEvent) => {
        this.logger.log({
          ServerEventMessage: serverEvent,
        });
      }),
      tap((createEvent) => this.store.dispatch(CommentAction.Created({ comment: createEvent.message_object.notification.body }))),
    ),
  { dispatch: false },
  );

  commentUpdatedEvent$ = createEffect(() =>
    this.serverEventsProviderService.getServerEvent(ServerEventType.CommentUpdated).pipe(
      tap((serverEvent) => {
        this.logger.log({
          ServerEventMessage: serverEvent,
        });
      }),
      tap((updateEvent) => this.store.dispatch(CommentAction.Updated({ updatedComment: updateEvent.message_object.notification.body }))),
    ),
  { dispatch: false },
  );

  commentDeletedEvent$ = createEffect(() =>
    this.serverEventsProviderService.getServerEvent(ServerEventType.CommentDeleted).pipe(
      tap((serverEvent) => {
        this.logger.log({
          ServerEventMessage: serverEvent,
        });
      }),
      tap((deleteEvent) => this.store.dispatch(CommentAction.Deleted({ deletedComment: deleteEvent.message_object.notification.body }))),
    ),
  { dispatch: false },
  );
}
